/**
 * indexItems — the bridge from a resolved model to the index's per-module item
 * models.
 *
 * The `index` representation renders each shown module as a live `<EmbeddedBoard>`
 * of its subspace (see ViewIndexView). To place an item it needs the module behind
 * an `IndexEntry.key` (name, type, description, child count, exposed interfaces,
 * aspects, and whether it has children ⇒ a subspace board) plus a **focal path
 * into the shared solved board root** so the embed can project that subspace, and
 * its dependency degree (depends-on / used-by) drawn from the edge graph.
 *
 * Keys match the engine's node universe exactly: the dotted qualified path
 * (`parent.child`), NEVER a `#id` — so an `IndexEntry.key` looks its module up
 * here directly, and edge `fromModulePath`/`toModulePath` re-key by `join(".")`.
 * (`moduleKey()` prefers the id and would mismatch; we walk the tree ourselves,
 * mirroring `buildNodeUniverse`.)
 */
import { serializeModel } from "@archlang/viewer-core";
import type { Module, DependencyEdge } from "@archlang/scene/arch-model";
import type { IndexEntry, ResolvedModel } from "@archlang/engine";

/** One aspect chip on an item — a `key: value` pair (e.g. `team: Payments`). */
export interface IndexAspect {
  readonly key: string;
  readonly value: string;
}

/** One index item — the module's compact detail (header · description · facts ·
 *  aspect chips) + its subspace focal path (null for a leaf, whose item is
 *  detail-only, no board). Built by {@link buildIndexItems}; consumed by
 *  ViewIndexView (the type lives here, not in the pure view). This is the "one
 *  source" a future module PAGE can also render (at full density). */
export interface IndexItemVM {
  /** Qualified key — React key + test hook + the board-root open target. */
  readonly key: string;
  /** The module's `#id`, when it has one (identity only; open roots by key). */
  readonly id?: string;
  readonly name: string;
  /** The kind keyword (`microservice`, `database`, …) shown under the name. */
  readonly typeName: string;
  /** Description, clamped in the item. */
  readonly description?: string;
  /** Direct submodule count (containers only; 0 for a leaf). */
  readonly childCount: number;
  /** Exposed-interface names (the surface the module offers); shown truncated. */
  readonly interfaces: readonly string[];
  /** Distinct downstream modules this one calls (out-degree in the edge graph). */
  readonly dependsOn: number;
  /** Distinct upstream modules that call this one (in-degree in the edge graph). */
  readonly usedBy: number;
  /** Aspect chips (team/zone/…); capped in the view. */
  readonly aspects: readonly IndexAspect[];
  /** Focal path into the shared root's subspace for this module, or null when the
   *  module is a leaf (no children ⇒ no board). */
  readonly focalPath: readonly string[] | null;
}

/** A serialized aspect value is a wrapped literal node (`{ kind, text, … }`), not
 *  a bare scalar — pull its display text for the chip; fall back to String(). */
function aspectValueText(value: unknown): string {
  if (value && typeof value === "object" && "text" in value) {
    return String((value as { text: unknown }).text);
  }
  return String(value);
}

/**
 * Index every module in a resolved model by its dotted qualified key — the same
 * key `computeIndexData` returns. Serializes to the scene wire form (structurally
 * compatible with `Module`), then walks the tree stamping `parent.child` keys.
 */
export function indexModulesByKey(model: ResolvedModel): ReadonlyMap<string, Module> {
  const out = new Map<string, Module>();
  const wire = serializeModel(model);
  const walk = (m: Module, parent: string | undefined): void => {
    const key = parent ? `${parent}.${m.name}` : m.name;
    out.set(key, m);
    for (const c of m.children ?? []) walk(c, key);
  };
  for (const r of wire.modules as unknown as Module[]) walk(r, undefined);
  return out;
}

/**
 * Live-filter items by a free-text query — the index header's inline search. A
 * case-insensitive substring match across the fields a person would search by:
 * the module name, its qualified key, the type keyword, its exposed interface
 * names, and its aspect keys/values. An empty/blank query returns the list
 * unchanged (same reference), so no-search stays allocation-free.
 */
export function filterIndexItems(
  items: readonly IndexItemVM[],
  query: string,
): readonly IndexItemVM[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((it) => {
    if (it.name.toLowerCase().includes(q)) return true;
    if (it.key.toLowerCase().includes(q)) return true;
    if (it.typeName.toLowerCase().includes(q)) return true;
    if (it.interfaces.some((i) => i.toLowerCase().includes(q))) return true;
    return it.aspects.some(
      (a) => a.key.toLowerCase().includes(q) || a.value.toLowerCase().includes(q),
    );
  });
}

/** Per-module dependency degree from the edge graph, keyed by qualified module
 *  key. `out.get(K)` = the distinct modules K calls; `in.get(K)` = the distinct
 *  modules that call K. Only real call dependencies (`source: "process"`) count —
 *  aspect edges are a colour overlay, not a dependency. Distinct NEIGHBOURS, not
 *  raw edges, so many interface calls between the same pair read as one link. */
export interface ModuleDegrees {
  readonly out: ReadonlyMap<string, ReadonlySet<string>>;
  readonly in: ReadonlyMap<string, ReadonlySet<string>>;
}

export function moduleDegrees(edges: readonly DependencyEdge[]): ModuleDegrees {
  const out = new Map<string, Set<string>>();
  const inc = new Map<string, Set<string>>();
  const add = (m: Map<string, Set<string>>, k: string, v: string): void => {
    let s = m.get(k);
    if (!s) m.set(k, (s = new Set()));
    s.add(v);
  };
  for (const e of edges) {
    if (e.source !== "process") continue;
    const from = e.fromModulePath.join(".");
    const to = e.toModulePath.join(".");
    if (!from || !to || from === to) continue;
    add(out, from, to);
    add(inc, to, from);
  }
  return { out, in: inc };
}

/**
 * Turn ordered index entries into item models, resolving each container module's
 * live-board focal path. A container's path is its CONTAINING space (model-derived
 * via `moduleFocalPath`, so it reaches below the lazy board's preview depth) plus
 * its own `${id}::space` — the exact focal path `asSource(root).at(...)` projects
 * that subspace at (proven against a real board in the tests). A leaf (no children)
 * has no subspace → null focal path → a detail-only item. `moduleFocalPath` is
 * absent when no board layout is mounted (e.g. a standalone view render) → every
 * item degrades to detail-only.
 *
 * Capped at `cap` items so a huge index never places thousands of slots at once;
 * the caller surfaces the "N of M" overflow (never a silent cut).
 */
export function buildIndexItems(
  entries: readonly IndexEntry[],
  modulesByKey: ReadonlyMap<string, Module>,
  moduleFocalPath: ((id: string) => readonly string[] | null) | undefined,
  cap: number,
  degrees?: ModuleDegrees,
): IndexItemVM[] {
  const out: IndexItemVM[] = [];
  for (const entry of entries) {
    if (out.length >= cap) break;
    const module = modulesByKey.get(entry.key);
    if (!module) continue; // shown key with no module (defensive) — skip
    const moduleId = entry.id ?? entry.key;
    const hasSubspace = module.children.length > 0;
    const containing = hasSubspace ? moduleFocalPath?.(moduleId) ?? null : null;
    const focalPath = containing ? [...containing, `${moduleId}::space`] : null;
    out.push({
      key: entry.key,
      ...(entry.id ? { id: entry.id } : {}),
      name: module.name,
      typeName: module.typeName,
      ...(module.description ? { description: module.description } : {}),
      childCount: module.children.length,
      interfaces: module.interfaces.map((i) => i.name),
      dependsOn: degrees?.out.get(entry.key)?.size ?? 0,
      usedBy: degrees?.in.get(entry.key)?.size ?? 0,
      aspects: module.aspects.map((a) => ({ key: a.path.join("."), value: aspectValueText(a.value) })),
      focalPath,
    });
  }
  return out;
}
